import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { CreditDebitNoteFigures } from "../../types/credit-debit-note-report.type";
import { SaleCenterClosingCreditDebitNote } from "../../types/sale-center-closing.type";
import { formatNumber } from "../../utils";
import {
  CREDIT_NOTE_LABEL,
  DEBIT_NOTE_LABEL,
  NET_LABEL,
  hasNotes,
  noteCountLabel,
} from "../CreditDebitNote/reportFigures";
import ClosingSectionBox from "./ClosingSectionBox";

const ROUTE_COLUMN_LABEL = "สายส่ง";
const TOTAL_ROW_LABEL = "รวม";
const BASIS_CAPTION = "ก่อน VAT · ตามวันที่ออกใบ";
const TOTAL_ROW_SX = { "& td": { fontWeight: 700 } };

interface ClosingCreditDebitNoteBoxProps {
  /** Absent on a backend that predates the notes. */
  notes: SaleCenterClosingCreditDebitNote | undefined;
}

interface NoteAmountCellProps {
  amount: number;
  count: number;
  isCredit?: boolean;
}

function NoteAmountCell({ amount, count, isCredit = false }: Readonly<NoteAmountCellProps>) {
  const amountColor = isCredit ? "error" : undefined;

  return (
    <TableCell align="right">
      <Typography component="span" variant="body2" color={amountColor}>
        {formatNumber(amount)}
      </Typography>{" "}
      <Typography component="span" variant="caption" color="text.secondary">
        ({noteCountLabel(count)})
      </Typography>
    </TableCell>
  );
}

interface FiguresRowProps {
  label: string;
  figures: CreditDebitNoteFigures;
  isTotal?: boolean;
}

function FiguresRow({ label, figures, isTotal = false }: Readonly<FiguresRowProps>) {
  const rowSx = isTotal ? TOTAL_ROW_SX : undefined;

  return (
    <TableRow sx={rowSx}>
      <TableCell>{label}</TableCell>
      <NoteAmountCell amount={figures.credit_note_amount} count={figures.credit_note_count} isCredit />
      <NoteAmountCell amount={figures.debit_note_amount} count={figures.debit_note_count} />
      <TableCell align="right">{formatNumber(figures.net_amount)}</TableCell>
    </TableRow>
  );
}

/**
 * The branch-day's ใบลดหนี้ / ใบเพิ่มหนี้ by route with the net they add to the day's sales, on the
 * report basis (ex-VAT, cash and credit alike). The cash ones are already inside ยอดที่ต้องโอนรวม,
 * so nothing here is added to the deposits. A day with no note draws nothing.
 */
export default function ClosingCreditDebitNoteBox({ notes }: Readonly<ClosingCreditDebitNoteBoxProps>) {
  if (!notes || !hasNotes(notes)) return null;

  return (
    <ClosingSectionBox
      title={`${CREDIT_NOTE_LABEL} / ${DEBIT_NOTE_LABEL}`}
      icon={ReceiptLongOutlinedIcon}
      accent="success"
    >
      <Typography variant="caption" color="text.secondary">
        {BASIS_CAPTION}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{ROUTE_COLUMN_LABEL}</TableCell>
            <TableCell align="right">{CREDIT_NOTE_LABEL}</TableCell>
            <TableCell align="right">{DEBIT_NOTE_LABEL}</TableCell>
            <TableCell align="right">{NET_LABEL}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notes.routes.map((route) => (
            <FiguresRow key={route.route_code} label={route.route_code} figures={route} />
          ))}
          <FiguresRow label={TOTAL_ROW_LABEL} figures={notes} isTotal />
        </TableBody>
      </Table>
    </ClosingSectionBox>
  );
}
